/* eslint-disable no-param-reassign */
import axios from 'axios';
import { scrollToFirstError } from '@/util/forms';
import authService from '@/api/auth-service';
import store from '../store';
import i18n from '../i18n/i18n-config';
import eventBus, { OPEN_SNACKBAR } from '../util/event-bus';
import router from '@/router';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@/util/local-storage';
import getApiEndpoint from '@/util/get-api-endpoint';

function getRequestKey(config) {
  return `${config.method}:${config.url}`;
}

function refreshToken() {
  if (store.state.auth.refreshTokenPromise) {
    return store.state.auth.refreshTokenPromise;
  }

  const token = getLocalStorageItem('refreshToken');
  if (!token) {
    return Promise.reject(new Error('Refresh token not found'));
  }

  const refreshingToken = getLocalStorageItem('refreshingToken');
  if (refreshingToken) {
    return Promise.reject(new Error('Refreshing token in another tab'));
  }

  setLocalStorageItem('refreshingToken', true);
  const tokenRefreshPromise = authService
    .refreshToken(token)
    .then((res) => {
      store.dispatch('auth/handleAuthData', res.data);
      return Promise.resolve(true);
    })
    .finally(() => {
      store.commit('auth/SET_REFRESH_TOKEN_PROMISE', null);
      removeLocalStorageItem('refreshingToken');
    });
  store.commit('auth/SET_REFRESH_TOKEN_PROMISE', tokenRefreshPromise);
  return tokenRefreshPromise;
}

const httpClient = axios.create({
  baseURL: getApiEndpoint(),
});

httpClient.interceptors.request.use((config) => {
  if (store.getters['permits/securityCode']) {
    config.headers.common['X-Permit-Post-Security-Code'] = store.getters['permits/securityCode'];
  }
  if (store.getters['auth/accessToken']) {
    config.headers.common.Authorization = `Bearer ${store.getters['auth/accessToken']}`;
  }
  if (store.getters['auth/impersonatingAs']) {
    config.headers.common['Impersonating-As'] = `${store.getters['auth/impersonatingAs']}`;
  }

  config.headers.common.Locale = store.state.settings.locale;
  store.dispatch('setPendingRequest', getRequestKey(config));
  return config;
});

httpClient.interceptors.response.use(
  (res) => {
    store.dispatch('removePendingRequest', getRequestKey(res.config));
    return res;
  },
  (error) => {
    if (!error?.response?.config) {
      eventBus.$emit(OPEN_SNACKBAR, {
        text: i18n.t('network_error'),
        timeout: 10000,
      });
      return Promise.reject(error);
    }
    const requestKey = getRequestKey(error.response.config);
    store.dispatch('removePendingRequest', requestKey);
    switch (error.response.status) {
      case 401:
        if (requestKey === 'post:oauth/token') {
          // failed to refresh token
          return Promise.reject(error);
        }
        return refreshToken()
          .then(() => {
            error.config.headers.Authorization = `Bearer ${store.getters['auth/accessToken']}`;
            return axios.request(error.config);
          })
          .catch((err) => {
            if (
              err?.response?.status === 401 ||
              err.toString().includes('Refresh token not found')
            ) {
              if (store.state.auth.currentUser && store.state.auth.accessToken) {
                store.dispatch('auth/logout');
              }

              eventBus.$emit(OPEN_SNACKBAR, {
                text: i18n.t('session_expired'),
                timeout: 10000,
              });
            }
            return Promise.reject(err);
          });
      case 409:
        if (error.response.data.necessary_password_change) {
          store.commit('auth/UPDATE_MUST_CHANGE_PASSWORD', true);
          router.push({ name: 'mustChangePassword' }).catch(() => {});
        } else if (error.response.data.necessary_profile_complete) {
          store.commit('auth/UPDATE_MUST_COMPLETE_PROFILE', true);
          router.push({ name: 'mustCompleteProfile'}).catch(() =>{});
        }
        return Promise.reject(error);
      case 422:
        // eslint-disable-next-line
        const whiteListedRequests = ['post:api/permits/living-and-working-places-validation'];

        if (!whiteListedRequests.includes(getRequestKey(error.response.config))) {
          setTimeout(() => {
            scrollToFirstError();
          });
          eventBus.$emit(OPEN_SNACKBAR, i18n.t(`http_errors.${error.response.status}`));
        }

        if (
          error.request.responseType === 'blob' &&
          error.response.data instanceof Blob &&
          error.response.data.type &&
          error.response.data.type.toLowerCase().indexOf('json') !== -1
        ) {
          // this check ensures, that we get validation errors from file response
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              error.response.data = JSON.parse(reader.result);
              resolve(Promise.reject(error));
            };

            reader.onerror = () => {
              reject(error);
            };

            reader.readAsText(error.response.data);
          });
        }

        return Promise.reject(error);
      case 403: {
        // 2FA case returns 403 specifically from oauth/token
        if (requestKey === 'post:oauth/token') {
          // No snackbar - let Login.vue handle it
          return Promise.reject(error);
        }
      }

      case 428: {
        // This one we also leave to the component (2FA setup_required)
        if (requestKey === 'post:oauth/token') {
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
      case 429:
      case 404:
        if (getRequestKey(error.response.config).includes('get:api/locations/')) {
          eventBus.$emit(OPEN_SNACKBAR, i18n.t('location_does_not_exist'));
        } else {
          eventBus.$emit(OPEN_SNACKBAR, {
            text: i18n.t(`http_errors.${error.response.status}`),
            timeout: 8000,
          });
        }
        return Promise.reject(error);
      case 500: {
        if (error.response.data.message && error.response.data.message.indexOf('login') > -1) {
          store.dispatch('auth/logout').catch(() => {});
        } else {
          eventBus.$emit(OPEN_SNACKBAR, i18n.t('http_errors.500'));
        }
        return Promise.reject(error);
      }
      default: {
        return Promise.reject(error);
      }
    }
  },
);

export default httpClient;
